import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

export default function ValidationOptionCards({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}) {
  return (
    <Box
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: `repeat(${Math.min(2, options.length)}, 1fr)`,
        },
        gap: 1.5,
      }}
    >
      {options.map((option) => {
        const selected = value === option.value;
        const Icon = option.Icon;

        return (
          <Box
            key={option.value}
            role="radio"
            aria-checked={selected}
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (!disabled) onChange(option.value);
            }}
            onKeyDown={(event) => {
              if (disabled) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onChange(option.value);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: 1.5,
              borderRadius: "8px",
              border: "1px solid",
              borderColor: selected ? "text.primary" : "divider",
              backgroundColor: selected ? "action.selected" : "action.hover",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "border-color 0.15s, background-color 0.15s",
              "&:hover": disabled
                ? undefined
                : {
                    borderColor: "text.disabled",
                  },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "text.primary",
                outlineOffset: 2,
              },
            }}
          >
            {Icon ? (
              <Icon
                sx={{
                  fontSize: 20,
                  color: selected ? "text.primary" : "text.secondary",
                  mt: 0.25,
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 500, color: "text.primary" }}
              >
                {option.label}
              </Typography>
              {option.description ? (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    lineHeight: 1.35,
                    mt: 0.25,
                  }}
                >
                  {option.description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

ValidationOptionCards.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      Icon: PropTypes.elementType,
    })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
};
